import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BadgeCheck, Phone } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import EnquiryForm from "@/components/site/EnquiryForm";
import Testimonials from "@/components/site/Testimonials";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ORDER_STEPS = [
  { title: "Send your enquiry", text: "Tell us quantities and colours — upload your artwork right in the form." },
  { title: "Free quote & mockup", text: "Within 24 hours you receive trade pricing and a digital mockup of your design." },
  { title: "Approve your sample", text: "A pre-production sample lets you check fabric, fit and finish before bulk." },
  { title: "Production & delivery", text: "Your order is made, quality-checked and delivered to your door in 7–14 days." },
];

const ASSURANCES = [
  "Free design & mockup service",
  "Pre-production sample approval",
  "UK quality control on every order",
  "Your labels, tags & packaging",
  "Tracked door-to-door delivery",
  "Factory-direct trade pricing",
];

export default function ProductPage() {
  const { productId } = useParams();
  const [content, setContent] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => setFailed(true));
  }, []);

  const product = content?.products?.find((p) => p.id === productId);

  useEffect(() => {
    if (!product) return;
    document.title = product.seo_title || `${product.name} — AZ Concepts UK Ltd`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && product.seo_description) meta.setAttribute("content", product.seo_description);
    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.seo_description || product.description,
      image: product.image,
      brand: { "@type": "Brand", name: "AZ Concepts UK Ltd" },
      url: `https://azconcepts.net/products/${product.id}`,
    };
    let s = document.getElementById("product-jsonld");
    if (!s) {
      s = document.createElement("script");
      s.id = "product-jsonld";
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(ld);
    if (product.faqs?.length) {
      const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: product.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      };
      let fs = document.getElementById("faq-jsonld");
      if (!fs) {
        fs = document.createElement("script");
        fs.id = "faq-jsonld";
        fs.type = "application/ld+json";
        document.head.appendChild(fs);
      }
      fs.textContent = JSON.stringify(faqLd);
    }
  }, [product]);

  const scrollToEnquiry = () => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" });

  if (failed)
    return (
      <div className="min-h-screen bg-[#0D0F12] text-slate-300 flex items-center justify-center font-body" data-testid="product-load-error">
        Unable to load right now. Please refresh.
      </div>
    );

  if (!content)
    return (
      <div className="min-h-screen bg-[#0D0F12] flex items-center justify-center" data-testid="product-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#FF5A1F] border-t-transparent animate-spin" />
      </div>
    );

  if (!product)
    return (
      <div className="min-h-screen bg-[#0D0F12] text-slate-100 font-body flex flex-col items-center justify-center gap-6" data-testid="product-not-found">
        <h1 className="font-heading text-3xl font-extrabold">Product range not found</h1>
        <Link to="/" data-testid="product-not-found-home-link" className="text-[#FF5A1F] font-bold hover:underline">← Back to all products</Link>
      </div>
    );

  const others = content.products.filter((p) => p.id !== productId);

  return (
    <div id="top" className="bg-[#0D0F12] text-slate-100 min-h-screen font-body" data-testid="product-page">
      <Header contact={content.contact} onQuote={scrollToEnquiry} products={content.products} />
      <main>
        <section className="max-w-7xl mx-auto px-6 pt-12 lg:pt-20 pb-16" data-testid="product-hero">
          <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft size={16} /> All products
          </Link>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/3]">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </motion.div>
            <div>
              <span className="text-xs font-bold text-[#FF5A1F] uppercase tracking-wider bg-[#FF5A1F]/10 rounded-full px-3 py-1" data-testid="product-moq-badge">
                {product.moq}
              </span>
              <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-none" data-testid="product-page-title">
                {product.name}
              </h1>
              <p className="mt-3 text-base md:text-lg text-slate-400">{product.tagline}</p>
              <p className="mt-5 text-base text-slate-300 leading-relaxed" data-testid="product-long-description">
                {product.long_description || product.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button data-testid="product-quote-button" onClick={scrollToEnquiry}
                  className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04810] text-white font-bold rounded-full px-7 py-3.5 transition-all hover:gap-3 shadow-lg shadow-[#FF5A1F]/25">
                  Get a Wholesale Quote <ArrowRight size={17} />
                </button>
                <a data-testid="product-call-button" href={`tel:${content.contact.phone}`}
                  className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 text-white font-semibold rounded-full px-7 py-3.5 transition-colors">
                  <Phone size={17} /> Call {content.contact.phone}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="product-trust-strip">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {content.stats.map((s, i) => (
              <div key={i} className="bg-[#161B22]/80 border border-white/10 rounded-2xl p-5 text-center" data-testid={`product-trust-stat-${i}`}>
                <p className="font-heading text-xl sm:text-2xl font-extrabold text-[#FF5A1F]">{s.value}</p>
                <p className="mt-1 text-xs sm:text-sm text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {product.specs?.length > 0 && (
          <section className="bg-[#161B22]/50 border-y border-white/5" data-testid="product-specs-section">
            <div className="max-w-7xl mx-auto px-6 py-16">
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="product-specs-heading">
                Specifications & options
              </h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.specs.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#161B22] border border-white/10 rounded-xl p-5" data-testid={`product-spec-${i}`}>
                    <BadgeCheck size={18} className="text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-300">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="product-order-steps">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">How It Works</p>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="order-steps-heading">
            Ordering is easy — 4 simple steps
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ORDER_STEPS.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                className="bg-[#161B22] border border-white/10 rounded-2xl p-6"
                data-testid={`order-step-${i}`}>
                <span className="font-heading text-3xl font-extrabold text-[#FF5A1F]/30">0{i + 1}</span>
                <h3 className="mt-3 font-heading font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 py-8" data-testid="product-confidence-section">
          <div className="bg-[#FF5A1F] rounded-3xl p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white" data-testid="confidence-heading">Order with confidence</h2>
              <p className="mt-3 text-white/85 text-sm sm:text-base">Every order from AZ Concepts comes with:</p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {ASSURANCES.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-sm font-semibold text-white">
                    <BadgeCheck size={16} className="shrink-0 mt-0.5" /> {a}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:text-right">
              <p className="font-heading text-xl font-extrabold text-white">Free quote within 24 hours</p>
              <p className="mt-1 text-white/85 text-sm">No obligation. No hidden costs.</p>
              <div className="mt-5 flex flex-wrap gap-3 lg:justify-end">
                <button data-testid="confidence-quote-button" onClick={scrollToEnquiry}
                  className="inline-flex items-center gap-2 bg-white text-[#E04810] font-bold rounded-full px-6 py-3 hover:bg-slate-100 transition-colors">
                  Get My Quote <ArrowRight size={16} />
                </button>
                <a data-testid="confidence-whatsapp-button" href={`https://wa.me/${content.contact.whatsapp}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center border border-white/50 text-white font-semibold rounded-full px-6 py-3 hover:bg-white/10 transition-colors">
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </section>

        {product.faqs?.length > 0 && (
          <section className="max-w-4xl mx-auto px-6 py-16" data-testid="product-faq-section">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Common Questions</p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="product-faq-heading">
              {product.name} — FAQs
            </h2>
            <Accordion type="single" collapsible className="mt-8">
              {product.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`product-faq-${i}`}>
                  <AccordionTrigger className="text-left font-heading font-bold text-slate-100 hover:text-[#FF5A1F]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        <Testimonials items={content.testimonials} />

        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="product-others-section">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Explore other ranges</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {others.map((o) => (
              <Link key={o.id} to={`/products/${o.id}`} data-testid={`other-product-link-${o.id}`}
                className="group bg-[#161B22] border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF5A1F]/40 transition-colors">
                <div className="h-28 overflow-hidden">
                  <img src={o.image} alt={o.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <p className="p-4 text-sm font-bold">{o.name}</p>
              </Link>
            ))}
          </div>
        </section>

        <EnquiryForm products={content.products} contact={content.contact} productInterest={product.id} />
      </main>
      <Footer contact={content.contact} products={content.products} />
      <FloatingCTA contact={content.contact} />
    </div>
  );
}
